import React, { useEffect, useState, useRef, Component } from 'react';
import { HeroSection } from '../components/HeroSection';
import { HowItWorks } from '../components/HowItWorks';
import { BenefitsSection } from '../components/BenefitsSection';
import { SituationsGrid } from '../components/SituationsGrid';
import { TestimonialsSection } from '../components/TestimonialsSection';
import { FAQSection } from '../components/FAQSection';
import { FinalCTA } from '../components/FinalCTA';
import { Footer } from '../components/Footer';
import { StickyMobileCTA } from '../components/StickyMobileCTA';
import { CashOfferForm } from '../components/CashOfferForm';
// Reusable wrapper for the "bubbled off" sections
function BubbleSection({
  children,
  className = ''
}: {
  children: React.ReactNode;
  className?: string;
}) {
  return <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 my-12 md:my-20" data-id="element-263">
      <div className={`rounded-3xl shadow-[0_1px_3px_rgba(0,0,0,0.08),0_8px_24px_rgba(0,0,0,0.04)] overflow-hidden ${className}`} data-id="element-264">
        {children}
      </div>
    </div>;
}
// Animated Counter Component
function AnimatedCounter({
  end,
  suffix = ''
}: {
  end: number;
  suffix?: string;
}) {
  const [count, setCount] = useState(0);
  const [isVisible, setIsVisible] = useState(false);
  const ref = useRef<HTMLDivElement>(null);
  useEffect(() => {
    const observer = new IntersectionObserver(([entry]) => {
      if (entry.isIntersecting) setIsVisible(true);
    }, {
      threshold: 0.1
    });
    if (ref.current) observer.observe(ref.current);
    return () => observer.disconnect();
  }, []);
  useEffect(() => {
    if (!isVisible) return;
    let startTimestamp: number;
    const duration = 1500;
    const step = (timestamp: number) => {
      if (!startTimestamp) startTimestamp = timestamp;
      const progress = Math.min((timestamp - startTimestamp) / duration, 1);
      // easeOutQuart
      const easeProgress = 1 - Math.pow(1 - progress, 4);
      setCount(Math.floor(easeProgress * end));
      if (progress < 1) window.requestAnimationFrame(step);
    };
    window.requestAnimationFrame(step);
  }, [isVisible, end]);
  return <div ref={ref} className="text-4xl md:text-5xl font-extrabold mb-2" data-id="element-265">
      {count}
      {suffix}
    </div>;
}
export function LandingPage() {
  return <main className="relative w-full overflow-hidden bg-stone-100" data-id="element-266">
      <HeroSection data-id="element-267" />

      {/* Standalone Stats Bar Bubble */}
      <BubbleSection className="bg-[#1B2A4A] text-white" data-id="element-268">
        <div className="py-12 px-6 md:px-12 lg:px-16" data-id="element-269">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-8 md:gap-0 divide-y md:divide-y-0 md:divide-x divide-white/20" data-id="element-270">
            <div className="pt-4 md:pt-0 md:px-8 first:pt-0 md:first:pl-0 text-center md:text-left" data-id="element-271">
              <AnimatedCounter end={500} suffix="+" data-id="element-272" />
              <div className="text-white/60 font-medium text-sm uppercase tracking-widest" data-id="element-273">
                Homes Bought
              </div>
            </div>
            <div className="pt-4 md:pt-0 md:px-8 text-center md:text-left" data-id="element-274">
              <div className="text-4xl md:text-5xl font-extrabold mb-2" data-id="element-275">A+</div>
              <div className="text-white/60 font-medium text-sm uppercase tracking-widest" data-id="element-276">
                Service Rating
              </div>
            </div>
            <div className="pt-4 md:pt-0 md:px-8 text-center md:text-left" data-id="element-277">
              <AnimatedCounter end={7} suffix=" Days" data-id="element-278" />
              <div className="text-white/60 font-medium text-sm uppercase tracking-widest" data-id="element-279">
                Avg Closing
              </div>
            </div>
            <div className="pt-4 md:pt-0 md:px-8 md:last:pr-0 text-center md:text-left" data-id="element-280">
              <AnimatedCounter end={24} suffix="hr" data-id="element-281" />
              <div className="text-white/60 font-medium text-sm uppercase tracking-widest" data-id="element-282">
                Offer Turnaround
              </div>
            </div>
          </div>
        </div>
      </BubbleSection>

      <BubbleSection className="bg-white" data-id="element-283">
        <HowItWorks data-id="element-284" />
      </BubbleSection>

      <BubbleSection className="bg-white" data-id="element-285">
        <BenefitsSection data-id="element-286" />
      </BubbleSection>

      <BubbleSection className="bg-white" data-id="element-287">
        <SituationsGrid data-id="element-288" />
      </BubbleSection>

      <BubbleSection className="bg-white" data-id="element-289">
        <TestimonialsSection data-id="element-290" />
      </BubbleSection>

      {/* Second CTA Block — Background Image Bubble */}
      <BubbleSection className="relative overflow-hidden" data-id="element-291">
        <div className="absolute inset-0 z-0" data-id="element-292">
          <img src="https://cdn.magicpatterns.com/uploads/7DnoPThazEC68Ei4F5hwDq/1E63B6E7-4D18-489C-BD0E-36E5FDB2B0F0.png" alt="Florida home with pool and palm trees" className="w-full h-full object-cover" data-id="element-293" />
          <div className="absolute inset-0 bg-[#1B2A4A]/80" data-id="element-294" />
          {/* Animated Shimmer Overlay */}
          <div className="absolute inset-0 bg-gradient-to-r from-transparent via-white/5 to-transparent animate-shimmer" data-id="element-295" />
        </div>
        <section className="relative z-10 py-20 md:py-28 px-6 md:px-12 lg:px-16" data-id="element-296">
          <div className="max-w-3xl mx-auto text-center" data-id="element-297">
            <h2 className="text-3xl md:text-4xl lg:text-5xl font-bold text-white mb-6 tracking-tight" data-id="element-298">
              Get Your No-Obligation Cash Offer Today
            </h2>
            <p className="text-lg text-slate-300 mb-12 font-light" data-id="element-299">
              Fill out the form below and we'll be in touch within 24 hours with
              a fair cash offer.
            </p>
            <div className="bg-white/95 backdrop-blur-xl p-8 md:p-10 rounded-2xl shadow-2xl border border-white/20 text-left relative" data-id="element-300">
              <div className="absolute -top-3 left-1/2 -translate-x-1/2 bg-[#E8913A] text-white text-xs font-bold uppercase tracking-wider py-1 px-3 rounded-full shadow-md whitespace-nowrap" data-id="element-301">
                ⚡ Limited spots available this month
              </div>
              <CashOfferForm buttonText="Request Offer Now" data-id="element-302" />
            </div>
          </div>
        </section>
      </BubbleSection>

      <BubbleSection className="bg-white" data-id="element-303">
        <FAQSection data-id="element-304" />
      </BubbleSection>

      <BubbleSection className="bg-[#1B2A4A]" data-id="element-305">
        <FinalCTA data-id="element-306" />
      </BubbleSection>

      <Footer data-id="element-307" />
      <StickyMobileCTA data-id="element-308" />
    </main>;
}